/**
 * Supabase client — the SDK is the source of truth for *session lifetime*
 * (storage, refresh, cross-tab coordination) but **not** for authentication.
 *
 * Authentication (OTP send/confirm) goes through our server's
 * `/v1/auth/send-otp` and `/v1/auth/confirm-otp` endpoints so the access-code
 * gate runs server-side and can't be bypassed by a client that talks to
 * Supabase directly. The server returns `{ accessToken, refreshToken }` on
 * confirm; the adapter then hands those to `supabase.auth.setSession(...)`
 * and the SDK takes over from there — auto-refresh ~30s before expiry,
 * persistence in localStorage, BroadcastChannel + Web Lock coordination so
 * two tabs never race on the same refresh token (which the previous
 * hand-rolled adapter did, causing Supabase's reuse-detection to revoke
 * the whole token family and log everyone out — see PR #172 fallout).
 *
 * No migration from the pre-SDK `putty_auth` localStorage key: anyone with
 * an active hand-rolled session will be bounced to the login screen once
 * on first load after this ships. Given the hand-rolled adapter was
 * already logging people out semi-randomly, one final re-login is no
 * worse than the status quo and keeps this file simple.
 *
 * Why `@supabase/auth-js` (and not `@supabase/supabase-js`):
 *
 *   The umbrella `createClient()` from `@supabase/supabase-js` always
 *   instantiates `RealtimeClient`, `StorageClient`, `FunctionsClient`,
 *   and `PostgrestClient` even when the caller only uses auth — pulling
 *   their full code paths and the `ws` browser shim into the main
 *   bundle (visible as a `realtime-js` chunk in the dev build's
 *   webpack stats). The viewer never touches any of those subsystems;
 *   only `supabase.auth.*` is consumed. Switching to `AuthClient`
 *   from the underlying `@supabase/auth-js` package keeps the surface
 *   we use and drops the dead-weight subsystems from the bundle.
 *
 *   The `auth-adapter.ts` consumer code reads `supabase.auth.X` — we
 *   keep that shape via a thin facade so the swap is a one-file
 *   change.
 */
import { AuthClient } from "@supabase/auth-js";
import { viewerConfig } from "@/viewer-config";
/**
 * Storage key for the SDK-managed session. Distinct from the legacy
 * `putty_auth` key the pre-SDK adapter wrote to — the shapes are
 * incompatible, so the SDK can't read what's there and there's no
 * benefit to reusing the same slot.
 */
const SDK_STORAGE_KEY = "putty_supabase_auth";
const authClient = new AuthClient({
    url: `${viewerConfig.supabase.url}/auth/v1`,
    headers: {
        apikey: viewerConfig.supabase.anonKey,
        Authorization: `Bearer ${viewerConfig.supabase.anonKey}`,
    },
    // Background timer refreshes ~30s before `exp`. Also re-runs on
    // `visibilitychange` when a backgrounded tab returns to the
    // foreground, so a tab that was throttled doesn't wake up with
    // a dead JWT.
    autoRefreshToken: true,
    // Persist to localStorage. Combined with the SDK's internal
    // BroadcastChannel + Web Lock, this keeps multiple tabs in sync
    // and serialises refresh so the rotating refresh token can't be
    // burned in parallel.
    persistSession: true,
    // We don't use OAuth providers that round-trip through a URL
    // fragment, so this is wasted work + an extra storage write on
    // every reload.
    detectSessionInUrl: false,
    storageKey: SDK_STORAGE_KEY,
});
/**
 * Thin facade matching the `{ auth: ... }` shape the existing
 * `auth-adapter.ts` consumer reads. Keeps the consumer untouched
 * across the realtime/storage/functions trim.
 */
export const supabase = { auth: authClient };
